
import { Component, Prop } from 'vue-property-decorator';
import { InputType } from '@/enums/InputType';
import { Permission } from '@/enums/Permission';
import VFormBuilder from '@/components/shared/form/VFormBuilder.vue';
import StackedForm from '@/mixins/StackedForm';
import { mixins } from 'vue-class-component';
import { Article } from '@/interfaces/models/Article';
import { OptionGroup } from '@/interfaces/models/OptionGroup';
import { cloneDeep, hasPermission } from '@/util/helper';
import { OptionArticle } from '@/interfaces/models/OptionArticle';
import { SelectItem } from '@/interfaces/components/SelectItem';
import VHeadlineAction from '@/components/shared/VHeadlineAction.vue';
import ArticleDefaultsForm from '@/components/article/form/ArticleDefaultsForm.vue';
import EditOptionDialog from '@/components/article/EditOptionDialog.vue';
import { namespace } from 'vuex-class';
import { ItemGroup } from 'vuetify';

const foodcard = namespace('foodcard');

interface Group {
  group: string | null;
  articles: string[];
}

interface Mapped {
  article: string;
  group: string;
  printerSortOrder: number;
  quantity: number;
}

interface Default {
  group: string;
  articles: string[];
  key: string;
  mapped: Mapped[];
}

interface Dependency {
  group: string | null;
  dependencies: DependencyItem[];
}

interface DependencyItem {
  groupArticles: string[];
}

@Component({
  components: { ArticleDefaultsForm, VHeadlineAction, VFormBuilder, EditOptionDialog },
})
export default class OptionFormDependencies extends mixins(StackedForm) {
  @Prop({ type: Array, required: true }) public options!: OptionGroup[];
  @Prop({ type: Array, required: true }) public articles!: Article[];

  public mappedGroups: Group[] = [];

  public groupDependencies: Dependency[] = [];

  public defaults: Default[] = [];

  public $refs!: {
    defaults: InstanceType<typeof ArticleDefaultsForm> & { getData: () => Default[]; defaults: Default[] };
    recommendations: InstanceType<typeof ArticleDefaultsForm> & { getData: () => Default[]; defaults: Default[] };
    editOptionDialog: InstanceType<typeof EditOptionDialog> & { getData: () => any };
  };

  public getGroup(id: string): OptionGroup | undefined {
    return this.optionsFiltered.find((og: OptionGroup) => og._id === id);
  }

  public sortGroup(optionGroups: string[] = []) {
    const sorted = [];

    for (const og of optionGroups) {
      const a = this.getGroup(og);
      if (a) {
        sorted.push(a);
      }
    }

    return sorted.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  get items() {
    return [
      {
        name: 'groups',
        type: InputType.Hidden,
        label: 'article.form.options',
        items: this.optionItems,
        change: this.onGroupsChange,
        multiple: true,
      },
    ];
  }

  public getOptionArticles(form: { groups: string[] }) {
    const articles: SelectItem[] = [];
    const groups: OptionGroup[] = this.optionsFiltered.filter((o: OptionGroup) => form.groups.indexOf(o._id) > -1);
    for (const group of groups) {
      for (const art of group.articles) {
        articles.push({
          value: art._id,
          text: this.$options.filters!.localized(art.name),
        });
      }
    }

    return articles;
  }

  get optionsFiltered() {
    if (hasPermission(Permission.OPTION_ARTICLE_VIEW_VISIBLE_ONLY, true)) {
      return this.options.filter((o: OptionGroup) => o.visible);
    }

    return this.options;
  }

  get optionItems() {
    return this.optionsFiltered.map((o: OptionGroup) => {
      return {
        value: o._id,
        text: this.$options.filters!.localized(o.name),
      };
    });
  }

  get articleItems() {
    return this.articles.map((a: Article) => {
      return {
        value: a._id,
        text: this.$options.filters!.localized(a.name),
      };
    });
  }

  public mounted() {
    // @ts-ignore
    if (this.initialValues) {
      // @ts-ignore
      if (this.initialValues.mappedGroups) {
        // @ts-ignore
        this.mappedGroups.push(...cloneDeep(this.initialValues.mappedGroups));
      }
      // @ts-ignore
      if (this.initialValues.groupDependencies) {
        // @ts-ignore
        this.groupDependencies.push(...cloneDeep(this.initialValues.groupDependencies));
      }
    }
  }

  public getMainArticle(form: any) {
    if (form.main) {
      const main: Article | undefined = this.articles.find((art: Article) => art._id === form.main);

      if (main) {
        const groups: OptionGroup[] = [];

        for (const g of main.groups) {
          const group: OptionGroup | undefined = this.getGroup(g as unknown as string);
          if (group) {
            groups.push(group);
          }
        }

        return { ...main, groups };
      }
    }

    return null;
  }

  public onGroupsChange(value: string[]) {
    this.$refs.defaults.defaults = this.$refs.defaults.defaults.filter((d: Default) => value.indexOf(d.group) !== -1);
    this.mappedGroups = this.mappedGroups.filter((g: Group) => value.indexOf(g.group!) !== -1);
    this.groupDependencies = this.groupDependencies.filter((gd: Dependency) => value.indexOf(gd.group!) !== -1);
    const articles: string[] = this.getOptionArticles({ groups: value }).map((i: SelectItem) => i.value) as string[];
    for (const groupDep of this.groupDependencies) {
      for (const dep of groupDep.dependencies) {
        const toRemove: number[] = [];
        for (const art of dep.groupArticles) {
          const index: number = articles.indexOf(art);
          if (index === -1) {
            toRemove.push(index);
          }
        }
        for (const r of toRemove) {
          dep.groupArticles.splice(r, 1);
        }
      }
    }
  }

  public getRulesForDep(dep: DependencyItem, i: number, execeptJ: number): string {
    const rules: string[] = ['required'];
    const fields: string[] = [];
    for (let index: number = 0; index < dep.groupArticles.length; index += 1) {
      if (index !== execeptJ) {
        fields.push(`@articles-${i}-${index}`);
      }
    }
    if (fields.length > 0) {
      rules.push(`unique_dependency:${fields.join(',')}`);
    }

    return rules.join('|');
  }

  public getGroupArticles(form: any, group: string) {
    const main: Article = this.getMainArticle(form)!;
    if (main) {
      const optionGroup: OptionGroup = (main.groups as OptionGroup[]).find((g: OptionGroup) => g._id === group)!;
      if (optionGroup) {
        return optionGroup.articles;
      }
    }

    return [];
  }

  public removeGroup(group: Group) {
    const index: number = this.mappedGroups.indexOf(group);
    if (index > -1) {
      this.mappedGroups.splice(index, 1);
    }
  }

  public removeGroupDependency(dep: Dependency) {
    const index: number = this.groupDependencies.indexOf(dep);
    if (index > -1) {
      this.groupDependencies.splice(index, 1);
    }
  }

  public removeDependency(group: Dependency, dep: DependencyItem) {
    const index: number = group.dependencies.indexOf(dep);

    if (index > -1) {
      group.dependencies.splice(index, 1);
    }
  }

  public addGroup() {
    this.mappedGroups.push({
      group: null,
      articles: [],
    });
  }

  public addGroupDependency() {
    this.groupDependencies.push({
      group: null,
      dependencies: [],
    });
  }

  public addDependency(group: Dependency) {
    group.dependencies.push({
      groupArticles: [],
    });
  }

  public getPossibleDependencyGroups(form: { groups: string[] }, ignoreGroup: string) {
    if (!form.groups || form.groups.length === 0) {
      return [];
    }
    const items: SelectItem[] = [];
    for (const group of form.groups) {
      if (!this.groupDependencies.find((dep: Dependency) => dep.group === group && group !== ignoreGroup)) {
        const selectItem: SelectItem = this.optionItems.find((o: SelectItem) => o.value === group)!;
        items.push(selectItem);
      }
    }

    return items;
  }

  public getArticle(group: string, article: string) {
    const item: OptionGroup = this.optionsFiltered.find((item: OptionGroup) => item._id === group)!;
    if (item) {
      return item.articles.find((art: OptionArticle) => art._id === article);
    }

    return null;
  }

  public getData() {
    return {
      // @ts-ignore
      ...this.$refs.form.form,
      groupDependencies: this.groupDependencies,
      mappedGroups: this.mappedGroups,
      defaults: this.$refs.defaults.getData(),
      recommendations: this.$refs.recommendations.getData(),
    };
  }

  public handleOpenModal(id: string) {
    // Set option to be edited
    // this.$refs.editOptionDialog.show({id})
    this.$refs.editOptionDialog.getOptionDetails(id);
    // Open option modal
    this.$refs.editOptionDialog.setShowModal(true);
  }
}
